import React, {useEffect, useRef, useState} from 'react';
import {HexColorPicker} from 'react-colorful';
import {useDispatch, useSelector} from "react-redux";
import Stack from "@mui/joy/Stack";
import {HeadingText} from "../../Components/StyledComponents";
import {Box} from '@mui/material';
import Button from '@mui/joy/Button';
import Divider from '@mui/joy/Divider';
import Input from '@mui/joy/Input';
import {MdContentCopy} from "react-icons/md";
import ColorizeIcon from '@mui/icons-material/Colorize';
import {changeEffectColor, changeSVGColor, colorToHex, hexStringToHext} from "../../utils/helpers";
import {setCanvasCursor} from "../../utils/fabricOverrides";
import colorIcon from '../../assets/icons/color-picker.ico'
import {CloseIcon} from "../../assets/icons";
import {setPanelKey} from "../../store/reducers/panleKey";
import ColorProps from "../../Components/ColorProps";

let selectedColor = '';
let colorApplied = false;

export function ColorPicker({className = '', ...props}) {
    const [colorCode, setColorCode] = useState('#997272');
    const [colors, setColors] = useState(['#000000', '#FF0000', '#FFFF00', '#3F51B5', '#F0FF00', '#8F51B5', '#F44336']); // Initial colors

    const {activeSetting} = useSelector((state) => state.canvas);
    const {activeColorSetting} = useSelector((state) => state.canvas);
    const {canvas} = useSelector((state) => state.canvas);
    const {activeObject} = useSelector((state) => state.canvas);
    const {colorType} = useSelector((state) => state.colorSlice);
    const {SVGColor} = useSelector((state) => state.colorSlice);

    const [isPointerDown, setIsPointerDown] = useState(false);
    const [currentColor, setCurrentColor] = useState('');
    const [isColorChanging, setIsColorChanging] = useState(false);
    const [colorDuringChange, setColorDuringChange] = useState('');
    const [isColorPickerEnabled, setIsColorPickerEnabled] = useState(false);

    const colorRef = useRef(props?.color);
    const colorBeforePickerIsEnabled = useRef();

    const dispatch = useDispatch()

    useEffect(() => {
        window.addEventListener('mouseup', mouseUpFunc);
        return () => window.removeEventListener('mouseup', mouseUpFunc);
    }, [canvas]);

    useEffect(() => {
        if (isPointerDown) {
            window.addEventListener('pointerup', onMouseUp);
        }
        return () => {
            window.removeEventListener('pointerup', onMouseUp);
        };
    }, [isPointerDown]);

    useEffect(() => {
        if (activeSetting) {
            const {
                shadow, textColor, stroke
            } = activeSetting;
            const {gradientColor} = activeColorSetting
            switch (colorType) {
                case 'shadow':
                    setCurrentColor(shadow?.color ? shadow?.color : '#FF0000')
                    break
                case 'outline':
                    setCurrentColor(activeObject?.stroke ? activeObject.stroke : '#ff0000')
                    break
                case 'text':
                    setCurrentColor(textColor ? textColor : '#ff0000')
                    break
                case 'gradient':
                    setCurrentColor(gradientColor ? gradientColor : '#ff0000')
                    break
                case 'svg':
                    setCurrentColor(SVGColor?.value ? SVGColor?.value : '#ff0000')
                    break
                default:
            }
        }
    }, [activeSetting, colorType]);

    const onColorPicking = (e) => {
        const color = getColorFromCoOrdinates(e);
        setCurrentColor(color);
        let newColor = '#' + color.toUpperCase();
        handleColorChange(newColor);
    };

    const onColorPicked = (e) => {
        const color = getColorFromCoOrdinates(e);
        setCurrentColor(color);
        let newColor = '#' + color.toUpperCase()
        handleColorChange(newColor)
        canvas.fire('object:modified', {target: activeObject});
        disableColorPickMode();
    };

    const onClickPicker = () => {
        if (!isColorPickerEnabled) {
            enableColorPicker();
        }
        setIsColorPickerEnabled(!isColorPickerEnabled);
    };

    const enableColorPicker = () => {
        if (canvas) {
            colorBeforePickerIsEnabled.current = currentColor;
            setCanvasCursor(canvas, `url("${colorIcon}") -5 25 , auto`);
            canvas.on('mouse:move', onColorPicking);
            canvas.on('mouse:down', onColorPicked);
            canvas.on('mouseout', onRevertColor);
        }
    };

    const onRevertColor = () => {
        if (colorBeforePickerIsEnabled.current) {
            setCurrentColor(hexStringToHext(colorBeforePickerIsEnabled.current));
        }
    };

    const disableColorPickMode = () => {
        if (canvas) {
            canvas.off('mouse:move', onColorPicking);
            canvas.off('mouse:down', onColorPicked);
            canvas.off('mouseout', onRevertColor);
            setCanvasCursor(canvas, 'default');
            setIsColorPickerEnabled(false);
        }
    };


    const getColorFromCoOrdinates = (e) => {
        if (!canvas) {
            return '';
        }
        const pointer = e.absolutePointer ? e.absolutePointer : e.pointer;
        const zoom = canvas.getZoom()
        const scaleForPointer = zoom * canvas.getRetinaScaling();
        const p = canvas.getContext('2d').getImageData(pointer.x * scaleForPointer, pointer.y * scaleForPointer, 1, 1).data;
        return colorToHex({r: p[0], g: p[1], b: p[2]});
    };

    const handleColorChange = (value) => {
        if (value === '#') value = '#000000'
        setCurrentColor(value);
        switch (colorType) {
            case 'text':
                textColorChange(value);
                break;
            case 'shadow':
                changeEffectColor(value, colorType, activeObject, canvas, dispatch, activeColorSetting)
                break;
            case 'outline':
                changeEffectColor(value, colorType, activeObject, canvas, dispatch, activeColorSetting)
                break;
            case 'gradient':
                changeEffectColor(value, colorType, activeObject, canvas, dispatch, activeColorSetting)
                break;
            case 'svg':
                changeSVGColor(value, SVGColor, canvas, dispatch, activeObject)
                break;
            default:
                textColorChange(value)
                break
        }
    };

    const parseColorForInputField = (value) => {
        return value?.slice(1).toUpperCase();
    };

    const onClickCopy = async () => {
        await navigator.clipboard.writeText(parseColorForInputField(currentColor).toUpperCase());
    };

    const mouseUpFunc = () => {
        if (!canvas || !selectedColor || colorApplied || !activeObject) return;
        canvas.fire('object:modified', {target: activeObject});
        colorApplied = true;
    };

    const textColorChange = (color) => {
        if (activeObject) {
            activeObject.set({fill: color});
            colorApplied = false;
            selectedColor = color;
            canvas.renderAll();
        }
    };

    const handleAddColor = () => {
        if (colorCode && !colors.includes(colorCode)) {
            setColors([...colors, colorCode]);
        }
    };

    const onMouseUp = () => {
        setCurrentColor(colorRef.current);
        window.removeEventListener('pointerup', onMouseUp);
        setIsPointerDown(false);
    };

    const hasAlphaNumericsOnly = (string) => {
        const regexExpression = /[^A-Z0-9]/gi;
        const matches = string.match(regexExpression);
        return !(matches && matches.length > 0);
    };

    const isValidInput = (value) => {
        if (value.length > 6) {
            return false;
        }
        return hasAlphaNumericsOnly(value);
    };

    const onChangeHandler = (value) => {
        if (!isValidInput(value)) {
            return;
        }
        if (!isColorChanging) {
            setIsColorChanging(true);
        }
        setColorDuringChange(value.toUpperCase());
        let newColor = '#' + value.toUpperCase()
        handleColorChange(newColor)
    };


    const getInputFieldText = () => {
        if (isColorChanging) {
            return colorDuringChange;
        }
        if (typeof currentColor === 'object') {
            setCurrentColor('#000000');
            return parseColorForInputField('#000000');
        }
        return parseColorForInputField(currentColor?.toUpperCase());
    };

    const closeTextPanel = () => {
        dispatch(setPanelKey('1'))
    }

    return (
        <>
            <div className='text-header' style={{padding: 0}}>
                <HeadingText>Color</HeadingText>
                <span onClick={closeTextPanel} className='cross-icon'><CloseIcon/></span>
            </div>
            <hr className="solid-divider"/>
            <Stack className="color-popup-title">
                <Stack className="color-wrapper" alignItems='center' justifyContent='space-between' direction='row'>
                    <div className="flex-center"><span className="body-xs-bold color-primary  ">Color </span></div>
                    <span className="color-preview" style={{background: `${currentColor}`}}></span>
                </Stack>
            </Stack>
            <div className={`${className} saturation`}>
                <HexColorPicker
                    color={currentColor}
                    onChange={(value) => {
                        handleColorChange(value);
                        colorRef.current = value;
                    }}
                    onPointerDown={() => {
                        setIsPointerDown(true);
                    }}
                />
            </div>
            <Box>
                <Box className='input-color-wrapper'
                     sx={{display: 'flex', alignItems: 'center', marginBottom: '20px', marginTop: '16px'}}>
                    <Stack direction='row' sx={{width: '100%'}} spacing={1}>
                        <Input
                            placeholder="Color"
                            startDecorator={{hash: '#'}['hash']}
                            onChange={(e) => onChangeHandler(e.target.value)}
                            value={getInputFieldText()}
                            onBlur={() => {
                                setIsColorChanging(false);
                            }}
                            endDecorator={
                                <React.Fragment>
                                    <Divider orientation="vertical"/>
                                    <span onClick={onClickCopy} style={{
                                        paddingLeft: '8px',
                                        cursor: 'pointer',
                                        display: 'flex'
                                    }}><MdContentCopy color='#38485D' size={17}/></span>
                                </React.Fragment>
                            }
                        />
                        <Button
                            onClick={onClickPicker}
                            sx={{width: '60%'}}
                            component="label"
                            role={undefined}
                            tabIndex={-1}
                            variant="outlined"
                            color="neutral"
                            size="sm"
                            startDecorator={
                                <ColorizeIcon fontSize={'small'}/>
                            }
                        >
                            Picker
                        </Button>
                    </Stack>
                </Box>
                <ColorProps currentColor={currentColor}/>
            </Box>
        </>
    );
}
